import React from 'react';
import { Card, Button, Space, Progress } from 'antd';
import { 
  LeftOutlined, 
  RightOutlined, 
  CheckOutlined, 
  CloseOutlined 
} from '@ant-design/icons';
import { OnboardingWizardProps } from '../types';
import OnboardingStepIndicator from './OnboardingStepIndicator';

const OnboardingWizard: React.FC<OnboardingWizardProps & { children: React.ReactNode }> = ({
  steps,
  currentStep,
  onStepChange,
  onNext,
  onPrevious,
  onSubmit,
  onCancel,
  isSubmitting = false,
  canProceed = true,
  orientation = 'horizontal',
  showProgress = true,
  children,
}) => {
  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  
  const completedSteps = steps.filter(step => step.isCompleted).length;
  const progressPercentage = Math.round((completedSteps / steps.length) * 100);

  const handleStepClick = (stepIndex: number) => {
    // Allow navigation to completed steps or the next immediate step
    const canNavigate = steps[stepIndex].isCompleted || 
                       stepIndex <= currentStep + 1;
    
    if (canNavigate) {
      onStepChange(stepIndex);
    }
  };

  // Debug logging
  console.log('=== ONBOARDING WIZARD RENDER ===');
  console.log('Orientation prop:', orientation);
  console.log('Will render vertical layout:', orientation === 'vertical');
  console.log('===============================');

  // Vertical orientation layout
  if (orientation === 'vertical') {
    return (
      <div className="onboarding-wizard vertical-orientation">
        {/* Vertical step indicator on the left */}
        <div style={{ width: '280px', flexShrink: 0 }}>
          <Card>
            {showProgress && (
              <div style={{ marginBottom: 16 }}>
                <Progress
                  percent={progressPercentage}
                  status="active"
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                  format={(percent) => `${completedSteps}/${steps.length} Steps`}
                />
              </div>
            )}
            <OnboardingStepIndicator
              steps={steps}
              currentStep={currentStep}
              onStepClick={handleStepClick}
              orientation="vertical"
            />
          </Card>
        </div>

        {/* Main content area */}
        <div style={{ flex: 1 }}>
          <Card>
            <div className="wizard-step-content">
              <div className="step-header" style={{ marginBottom: 24 }}>
                <h2>{currentStepData?.title}</h2>
                {currentStepData?.description && (
                  <p style={{ color: '#666' }}>{currentStepData.description}</p>
                )}
              </div>
              
              <div className="step-body">
                {children}
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="wizard-actions" style={{ 
              marginTop: 24, 
              paddingTop: 24, 
              borderTop: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <Space>
                <Button
                  icon={<CloseOutlined />}
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </Space>
              
              <Space>
                {!isFirstStep && (
                  <Button
                    icon={<LeftOutlined />}
                    onClick={onPrevious}
                    disabled={isSubmitting}
                  >
                    Previous
                  </Button>
                )}
                
                {!isLastStep ? (
                  <Button
                    type="primary"
                    icon={<RightOutlined />}
                    onClick={onNext}
                    disabled={!canProceed || isSubmitting}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={onSubmit}
                    loading={isSubmitting}
                    disabled={!canProceed}
                  >
                    Submit
                  </Button>
                )}
              </Space>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Horizontal orientation layout (default)
  return (
    <div className="onboarding-wizard">
      <div className="wizard-header">
        {showProgress && (
          <div className="wizard-progress-container">
            <div className="wizard-progress">
              <Progress
                percent={progressPercentage}
                status="active"
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <span className="progress-text">
                {completedSteps} of {steps.length} steps completed
              </span>
            </div>
          </div>
        )}
        
        <div className="wizard-steps-container">
          <OnboardingStepIndicator
            steps={steps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />
        </div>
      </div>

      <Card className="wizard-content">
        <div className="step-header">
          <h3 className="step-title">{currentStepData?.title}</h3>
          {currentStepData?.description && (
            <p className="step-description">{currentStepData.description}</p>
          )}
        </div>

        <div className="step-content">
          {children}
        </div>

        <div className="wizard-actions">
          <Space>
            <Button
              icon={<CloseOutlined />}
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            
            {!isFirstStep && (
              <Button
                icon={<LeftOutlined />}
                onClick={onPrevious}
                disabled={isSubmitting}
              >
                Previous
              </Button>
            )}
            
            {!isLastStep && (
              <Button
                type="primary"
                icon={<RightOutlined />}
                onClick={onNext}
                disabled={!canProceed || isSubmitting}
              >
                Next
              </Button>
            )}
            
            {isLastStep && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={onSubmit}
                loading={isSubmitting}
                disabled={!canProceed}
              >
                Submit
              </Button>
            )}
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default OnboardingWizard;
